<template>
  <section :class="isMobileLayout ? 'mx-auto max-w-[720px] space-y-3 pb-24' : 'mx-auto max-w-[1700px] space-y-10'">
    <div v-if="!isMobileLayout" class="panel-neo panel-neo--glassier rounded-2xl p-6">
      <h1 class="qts-title-1 text-white">Scan Equipment</h1>
      <p class="qts-body-2 mt-2 text-slate-300" :class="isMobileLayout ? 'text-xs' : ''">
        Use your desktop or phone camera to scan a barcode, select/create a model, and save the serial.
      </p>
    </div>

    <div v-else class="px-3 pt-3">
      <div class="flex flex-wrap items-center gap-2">
        <label
          class="inline-flex items-center gap-2 rounded-md border border-slate-700/70 bg-slate-900/75 px-2 py-1 text-[11px] text-slate-200"
        >
          <input v-model="mobileDesktopFlowEnabled" type="checkbox" class="h-3.5 w-3.5 accent-blue-500" />
          Desktop-style auto actions
        </label>
        <label
          class="inline-flex items-center gap-2 rounded-md border border-slate-700/70 bg-slate-900/75 px-2 py-1 text-[11px] text-slate-200"
        >
          <input v-model="mobileRemoteModeEnabled" type="checkbox" class="h-3.5 w-3.5 accent-blue-500" />
          Remote scanner mode
        </label>
        <label v-if="mobileRemoteModeEnabled" class="flex items-center gap-2 text-[11px] text-slate-300">
          <span class="shrink-0">Session</span>
          <input
            v-model.trim="remoteSessionCode"
            type="text"
            class="md3-input scan-input-surface h-8 w-[150px] text-xs"
            placeholder="warehouse-a"
          />
        </label>
      </div>
    </div>

    <div
      class="grid grid-cols-1 gap-6"
      :class="isMobileLayout ? '' : 'xl:grid-cols-[minmax(0,1100px)_minmax(360px,520px)] xl:items-stretch xl:justify-start'"
    >
      <div class="panel-neo panel-neo--glassier h-full rounded-2xl p-4 sm:p-6 space-y-4" :class="isMobileLayout ? 'max-w-none' : 'max-w-[1100px]'">
        <div class="flex flex-wrap items-end gap-3">
          <label v-if="!isMobileLayout" class="flex w-[260px] max-w-full flex-col gap-1 text-slate-300">
            <span class="qts-label-2">Camera</span>
            <div class="relative" @click.stop>
              <button
                type="button"
                class="scan-input-surface w-full h-10 rounded-md border px-3 text-left text-sm text-slate-100 flex items-center justify-between"
                @click="toggleCameraDropdown"
              >
                <span class="truncate">{{ selectedCameraLabel }}</span>
                <span class="text-slate-400">▾</span>
              </button>
              <transition name="dropdown-spring">
                <div
                  v-if="showCameraDropdown"
                  class="absolute z-[90] mt-2 w-full overflow-hidden rounded-md border border-slate-700 bg-slate-900/98 shadow-2xl"
                >
                  <button
                    v-if="!cameraDevices.length"
                    type="button"
                    class="w-full px-3 py-2 text-left text-sm text-slate-400"
                    @click="showCameraDropdown = false"
                  >
                    No camera devices found
                  </button>
                  <button
                    v-for="camera in cameraDevices"
                    :key="camera.deviceId"
                    type="button"
                    class="w-full px-3 py-2 text-left text-sm text-slate-100 hover:bg-slate-800/90"
                    @click="selectCamera(camera.deviceId)"
                  >
                    {{ camera.label || `Camera ${camera.deviceId.slice(0, 6)}` }}
                  </button>
                </div>
              </transition>
            </div>
          </label>
          <button
            type="button"
            class="md3-button-primary h-10 px-4 shadow-[0_10px_26px_rgba(59,130,246,0.28)]"
            :disabled="isScannerBusy || isScanning"
            @click="startManagedScan"
          >
            {{ isScannerBusy ? 'Opening Scanner…' : isScanning ? 'Scanning…' : 'Start Scan' }}
          </button>
          <span v-if="isScannerBusy || isScanning" class="text-xs text-slate-300">
            Press <span class="font-mono text-slate-100">Esc</span> to close scanner
          </span>
          <button
            v-if="isScanning"
            type="button"
            class="md3-button-secondary h-10 px-4"
            @click="stopScan"
          >
            Stop
          </button>
        </div>

        <div class="relative rounded-xl border border-slate-700/70 bg-slate-950/60 p-2">
          <video
            ref="videoEl"
            class="w-full rounded-lg bg-black object-cover"
            :class="isMobileLayout ? 'h-[68vh] min-h-[420px]' : 'h-[300px] sm:h-[380px]'"
            playsinline
            muted
          ></video>
          <div class="pointer-events-none absolute inset-2 overflow-hidden rounded-lg">
            <div
              class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-primary-300/85 shadow-[0_0_0_9999px_rgba(2,6,23,0.42)]"
              :class="isMobileLayout ? 'h-[28%] w-[72%]' : 'h-[22%] w-[62%]'"
            ></div>
            <div
              class="absolute left-1/2 top-[21%] -translate-x-1/2 rounded-full border border-slate-700/70 bg-slate-950/82 px-3 py-1 text-[11px] text-slate-100"
            >
              Center barcode inside guide
            </div>
          </div>
        </div>

        <p v-if="cameraError" class="text-sm text-red-300">{{ cameraError }}</p>
        <p v-else class="text-sm text-slate-400">
          {{ isMobileLayout ? 'Align code inside guide and hold steady.' : 'Tip: center the barcode in the guide and move closer until the bars are sharp.' }}
        </p>
      </div>

      <div
        v-if="!isMobileLayout || showAssignmentPanel"
        class="panel-neo panel-neo--glassier h-full rounded-2xl p-4 sm:p-6 space-y-3 overflow-y-auto"
        :class="isMobileLayout ? 'max-w-none' : 'max-w-[520px]'"
      >
        <div class="flex items-center justify-between gap-3">
          <div>
            <h2 class="qts-title-2 text-white">Smart Assignment</h2>
            <p class="text-xs text-slate-500">Managed serial intake and auto-routing.</p>
            <label
              v-if="!isMobileLayout"
              class="mt-2 inline-flex items-center gap-2 rounded-md border border-slate-700/70 bg-slate-900/55 px-2 py-1 text-[11px] text-slate-200"
            >
              <input v-model="desktopRemoteListenEnabled" type="checkbox" class="h-3.5 w-3.5 accent-blue-500" />
              Listen for remote scanner
            </label>
            <label
              v-if="!isMobileLayout && desktopRemoteListenEnabled"
              class="mt-2 flex items-center gap-2 text-[11px] text-slate-300"
            >
              <span class="shrink-0">Session</span>
              <input
                v-model.trim="remoteSessionCode"
                type="text"
                class="md3-input scan-input-surface h-8 w-[220px] text-xs"
                placeholder="Example: warehouse-a"
              />
            </label>
          </div>
          <button type="button" class="md3-button-secondary h-9 px-3 text-xs" @click="showScanInfoModal = true">
            How It Works
          </button>
        </div>
        <p v-if="remoteListenerError && !isMobileLayout" class="text-xs text-amber-300">
          {{ remoteListenerError }}
        </p>

        <div class="rounded-lg border border-primary-500/40 bg-primary-500/10 px-4 py-3">
          <div class="flex items-center justify-between gap-3">
            <p class="qts-label-2 text-primary-200">Last detected code</p>
            <button
              type="button"
              class="md3-button-secondary h-8 px-3 text-xs"
              :disabled="isScannerBusy || isScanning"
              @click="rescan"
            >
              Rescan
            </button>
          </div>
          <p class="mt-1 text-[17px] font-semibold text-slate-50 break-all leading-tight tracking-[0.01em]">
            {{ lastDetectedCode || 'No barcode scanned yet' }}
          </p>
        </div>

        <label class="flex flex-col gap-1 text-slate-300">
          <span class="qts-label-2">Find equipment model</span>
          <input
            v-model.trim="modelSearch"
            type="text"
            class="md3-input scan-input-surface h-10"
            placeholder="Search by name, make, or model"
          />
        </label>

        <label class="flex flex-col gap-1 text-slate-300">
          <span class="qts-label-2">Equipment model</span>
          <div class="relative" @click.stop>
            <button
              type="button"
              class="scan-input-surface w-full h-10 rounded-md border px-3 text-left text-sm text-slate-100 flex items-center justify-between"
              @click="toggleModelDropdown"
            >
              <span class="truncate">{{ selectedModelLabel }}</span>
              <span class="text-slate-400">▾</span>
            </button>
            <transition name="dropdown-spring">
              <div
                v-if="showModelDropdown"
                class="absolute z-[90] mt-2 w-full max-h-64 overflow-y-auto rounded-md border border-slate-700 bg-slate-900/98 shadow-2xl"
              >
                <button
                  type="button"
                  class="w-full px-3 py-2 text-left text-sm text-slate-100 hover:bg-slate-800/90"
                  @click="selectModel(0)"
                >
                  Select a model
                </button>
                <button
                  v-for="type in filteredTypes"
                  :key="type.id"
                  type="button"
                  class="w-full px-3 py-2 text-left text-sm text-slate-100 hover:bg-slate-800/90"
                  @click="selectModel(Number(type.id))"
                >
                  {{ type.name }} · {{ type.make }} {{ type.model }}
                </button>
              </div>
            </transition>
          </div>
        </label>

        <div class="rounded-lg border border-slate-700/70 bg-slate-900/50 p-3 space-y-2.5">
          <div class="flex items-center justify-between gap-3">
            <p class="qts-label-2 text-slate-200">Create model quickly</p>
            <button type="button" class="text-xs text-primary-300 hover:text-primary-200" @click="showQuickCreate = !showQuickCreate">
              {{ showQuickCreate ? 'Hide' : 'Show' }}
            </button>
          </div>
          <div v-if="showQuickCreate" class="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            <input v-model.trim="quickCreateForm.name" class="md3-input scan-input-surface h-10" placeholder="Display name" />
            <input v-model.trim="quickCreateForm.make" class="md3-input scan-input-surface h-10" placeholder="Make" />
            <input v-model.trim="quickCreateForm.model" class="md3-input scan-input-surface h-10 sm:col-span-2" placeholder="Model" />
            <select v-model.number="quickCreateForm.categoryId" class="md3-input scan-input-surface scan-select-dark h-10 sm:col-span-2">
              <option :value="0" class="bg-slate-900 text-slate-100">Category (optional)</option>
              <option v-for="category in categories" :key="category.id" :value="category.id" class="bg-slate-900 text-slate-100">
                {{ category.name }}
              </option>
            </select>
            <button
              type="button"
              class="md3-button-secondary h-10 px-4 sm:col-span-2"
              :disabled="creatingModel"
              @click="createQuickModel"
            >
              {{ creatingModel ? 'Creating…' : 'Create Model' }}
            </button>
          </div>
        </div>

        <label class="flex flex-col gap-1 text-slate-300">
          <span class="qts-label-2">Serial status</span>
          <select v-model="serialStatus" class="md3-input scan-input-surface scan-select-dark h-10">
            <option v-for="status in serialStatusOptions" :key="status" :value="status" class="bg-slate-900 text-slate-100">
              {{ status }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-slate-300">
          <span class="qts-label-2">Notes (optional)</span>
          <textarea
            v-model.trim="serialNotes"
            class="md3-input scan-input-surface min-h-[90px]"
            placeholder="Example: scanned at warehouse intake"
          ></textarea>
        </label>

        <div class="pt-1">
          <button
            type="button"
            class="md3-button-primary h-10 w-full disabled:opacity-60 disabled:cursor-not-allowed"
            :disabled="!canSaveSerial || savingSerial"
            @click="saveScannedSerial"
          >
            {{ savingSerial ? 'Saving…' : 'Save Serial' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="isMobileLayout"
      class="fixed bottom-4 left-1/2 z-[1200] w-[calc(100%-1rem)] max-w-[680px] -translate-x-1/2 rounded-xl border border-slate-700/70 bg-slate-950/95 p-2 shadow-2xl backdrop-blur"
    >
      <div class="grid grid-cols-3 gap-2">
        <button
          type="button"
          class="md3-button-primary h-11 px-3 text-sm"
          :disabled="isScannerBusy || isScanning"
          @click="startManagedScan"
        >
          {{ isScannerBusy ? 'Opening…' : isScanning ? 'Scanning…' : 'Start Scan' }}
        </button>
        <button
          type="button"
          class="md3-button-secondary h-11 px-3 text-sm"
          :disabled="!isScanning && !isScannerBusy"
          @click="cancelManagedScan"
        >
          {{ isScannerBusy ? 'Close' : 'Stop' }}
        </button>
        <button
          type="button"
          class="md3-button-secondary h-11 px-3 text-sm"
          @click="showAssignmentPanel = !showAssignmentPanel"
        >
          {{ showAssignmentPanel ? 'Hide Panel' : 'Assign' }}
        </button>
      </div>
    </div>

    <CheckinModal
      v-if="showCheckinModal && autoFlowCheckoutId"
      :checkout-id="autoFlowCheckoutId"
      @close="showCheckinModal = false"
      @saved="handleAutoFlowSaved"
    />
    <ShippingLabelModal
      v-if="showShippingLabelModal && autoFlowTypeId"
      :make="autoFlowMake"
      :model="autoFlowModel"
      :serial="autoFlowSerial"
      :checkout-id="autoFlowCheckoutId || undefined"
      :equipment-id="autoFlowTypeId"
      @close="showShippingLabelModal = false"
      @saved="handleAutoFlowSaved"
    />

    <div
      v-if="showUnknownSerialModal"
      class="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      @click.self="showUnknownSerialModal = false"
    >
      <div class="glass-strong w-full max-w-2xl rounded-xl border border-slate-700/70 p-5 space-y-4">
        <h3 class="text-xl font-semibold text-white">Serial Not Found</h3>
        <p class="text-sm text-slate-300">
          <span class="font-mono text-slate-100">{{ pendingUnknownSerial }}</span> is not in TestDrive yet.
          Choose a model or quick-create one, then save this serial.
        </p>
        <label class="flex flex-col gap-1 text-slate-300">
          <span class="text-xs">Equipment model</span>
          <select v-model.number="selectedEquipmentTypeId" class="md3-input scan-input-surface scan-select-dark h-10">
            <option :value="0" class="bg-slate-900 text-slate-100">Select a model</option>
            <option v-for="type in filteredTypes" :key="type.id" :value="type.id" class="bg-slate-900 text-slate-100">
              {{ type.name }} · {{ type.make }} {{ type.model }}
            </option>
          </select>
        </label>
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <button
            type="button"
            class="md3-button-secondary h-10 px-3"
            @click="openManualFromUnknown"
          >
            Open In Manual Form
          </button>
          <button
            type="button"
            class="md3-button-secondary h-10 px-3"
            @click="openQuickCreateFromUnknown"
          >
            Create Model Quickly
          </button>
          <button
            type="button"
            class="md3-button-primary h-10 px-3 disabled:opacity-60"
            :disabled="!selectedEquipmentTypeId || savingSerial"
            @click="saveUnknownSerialNow"
          >
            Save New Serial
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="showScanInfoModal"
      class="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      @click.self="showScanInfoModal = false"
    >
      <div class="glass-strong w-full max-w-2xl rounded-xl border border-slate-700/70 p-6 space-y-5">
        <h3 class="text-2xl font-semibold text-white">Smart Scan Flow</h3>
        <p class="text-base text-slate-200 leading-relaxed">
          Scan runs in auto mode. TestDrive decides the next step so you can move fast.
        </p>
        <ul class="space-y-3 text-base text-slate-100 list-disc pl-6 leading-relaxed">
          <li>If serial is checked out, it opens Check In.</li>
          <li>If serial exists but is not checked out, it opens Shipping Label assignment.</li>
          <li>If serial is new, it opens model assignment or quick-create model.</li>
          <li>You can always use the assignment controls manually on this page.</li>
          <li>Enable Remote scanner mode on phone + listener on desktop to drive desktop popups from mobile scans.</li>
        </ul>
        <p class="text-sm text-slate-300">
          Keyboard shortcut: <span class="font-mono text-slate-100">Ctrl+Shift+S</span> starts scan.
        </p>
        <div class="flex justify-end">
          <button type="button" class="md3-button-primary h-10 px-4" @click="showScanInfoModal = false">
            Got it
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="showDiagnosticsPanel"
      class="panel-neo panel-neo--glassier rounded-2xl p-4 sm:p-6 space-y-3"
    >
      <div class="flex items-center justify-between gap-3">
        <div>
          <h2 class="qts-title-2 text-white">Live Scan Diagnostics</h2>
          <p class="text-xs text-slate-400">
            Last {{ scanEvents.length }} scan decisions from `app.scan_events`.
          </p>
        </div>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="md3-button-secondary h-9 px-3 text-xs"
            :disabled="scanEventsLoading"
            @click="loadScanEvents"
          >
            {{ scanEventsLoading ? 'Refreshing…' : 'Refresh' }}
          </button>
          <span class="text-[11px] text-slate-500">
            Updated {{ diagnosticsLastUpdatedLabel }}
          </span>
        </div>
      </div>

      <div class="overflow-x-auto rounded-lg border border-slate-700/70">
        <table class="min-w-full text-xs text-slate-200">
          <thead class="bg-slate-900/70 text-slate-300">
            <tr>
              <th class="px-3 py-2 text-left font-medium">Time</th>
              <th class="px-3 py-2 text-left font-medium">Route</th>
              <th class="px-3 py-2 text-left font-medium">Code</th>
              <th class="px-3 py-2 text-left font-medium">Serial ID</th>
              <th class="px-3 py-2 text-left font-medium">Checkout ID</th>
              <th class="px-3 py-2 text-left font-medium">Serial Status</th>
              <th class="px-3 py-2 text-left font-medium">Checkout Status</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="eventRow in scanEvents"
              :key="eventRow.id"
              class="border-t border-slate-800/80"
            >
              <td class="px-3 py-2 whitespace-nowrap">{{ formatEventTime(eventRow.created_at) }}</td>
              <td class="px-3 py-2">
                <span
                  class="inline-flex items-center rounded px-2 py-0.5 text-[11px]"
                  :class="routeBadgeClass(eventRow.route)"
                >
                  {{ eventRow.route }}
                </span>
              </td>
              <td class="px-3 py-2 font-mono text-[11px] break-all max-w-[320px]">
                {{ eventRow.scanned_code }}
              </td>
              <td class="px-3 py-2">{{ eventRow.equipment_serial_id ?? '—' }}</td>
              <td class="px-3 py-2">{{ eventRow.checkout_id ?? '—' }}</td>
              <td class="px-3 py-2">{{ eventRow.metadata?.serial_status || '—' }}</td>
              <td class="px-3 py-2">{{ eventRow.metadata?.checkout_status || '—' }}</td>
            </tr>
            <tr v-if="!scanEventsLoading && !scanEvents.length">
              <td colspan="7" class="px-3 py-3 text-slate-400">No scan events yet.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-if="scanEventsError" class="text-xs text-red-300">{{ scanEventsError }}</p>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { BarcodeFormat, DecodeHintType, NotFoundException } from '@zxing/library'
import { supabase } from '@/lib/supabaseClient'
import { createEquipmentSerial, createEquipmentTypeWithDetails } from '@/lib/inventoryApi'
import { useInventoryStore } from '@/stores/inventory'
import type { EquipmentStatus } from '@/types/inventory'
import { resolveScannedCode } from '@/lib/appApi'
import CheckinModal from '@/components/modals/CheckinModal.vue'
import ShippingLabelModal from '@/components/modals/ShippingLabelModal.vue'
import { useAuth0Store } from '@/stores/auth0'

const inventoryStore = useInventoryStore()
const auth0Store = useAuth0Store()
const videoEl = ref<HTMLVideoElement | null>(null)
const scanner = ref<BrowserMultiFormatReader | null>(null)
const cameraDevices = ref<MediaDeviceInfo[]>([])
const selectedCameraId = ref('')
const isScanning = ref(false)
const cameraError = ref('')
const scannedSessionCodes = ref<string[]>([])
const showCheckinModal = ref(false)
const showShippingLabelModal = ref(false)
const showUnknownSerialModal = ref(false)
const pendingUnknownSerial = ref('')
const autoFlowCheckoutId = ref<number | null>(null)
const autoFlowTypeId = ref<number | null>(null)
const autoFlowMake = ref('')
const autoFlowModel = ref('')
const autoFlowSerial = ref('')
const showScanInfoModal = ref(false)
const showAssignmentPanel = ref(true)
const scanEvents = ref<Array<{
  id: number
  scanned_code: string
  route: 'existing_checked_out' | 'existing_not_checked_out' | 'new_serial'
  equipment_serial_id: number | null
  checkout_id: number | null
  metadata: { serial_status?: string; checkout_status?: string } | null
  created_at: string
}>>([])
const scanEventsLoading = ref(false)
const scanEventsError = ref('')
const diagnosticsLastUpdatedAt = ref<Date | null>(null)
const isScannerBusy = ref(false)
const showCameraDropdown = ref(false)
const showModelDropdown = ref(false)
let diagnosticsPollTimer: number | null = null
const isMobileLayout = ref(false)
const mobileDesktopFlowEnabled = ref(false)
const MOBILE_DESKTOP_FLOW_KEY = 'testdrive:mobile-desktop-flow-enabled'
const mobileRemoteModeEnabled = ref(false)
const desktopRemoteListenEnabled = ref(true)
const remoteSessionCode = ref('warehouse-a')
const MOBILE_REMOTE_MODE_KEY = 'testdrive:mobile-remote-mode-enabled'
const DESKTOP_REMOTE_LISTEN_KEY = 'testdrive:desktop-remote-listen-enabled'
const REMOTE_SESSION_CODE_KEY = 'testdrive:remote-session-code'
let remotePollTimer: number | null = null
const remoteLastSeenEventId = ref(0)
const remoteListenerError = ref('')
const remoteListenerBusy = ref(false)

const categories = ref<Array<{ id: number; name: string }>>([])
const modelSearch = ref('')
const selectedEquipmentTypeId = ref(0)
const lastDetectedCode = ref('')
const serialStatus = ref<EquipmentStatus>('available')
const serialNotes = ref('')
const savingSerial = ref(false)

const showQuickCreate = ref(false)
const creatingModel = ref(false)
const quickCreateForm = ref({
  name: '',
  make: '',
  model: '',
  categoryId: 0,
})

type NativeBarcodeDetectorResult = { rawValue?: string }
type NativeBarcodeDetectorInstance = {
  detect(source: ImageBitmapSource): Promise<NativeBarcodeDetectorResult[]>
}
type NativeBarcodeDetectorConstructor = {
  new (options?: { formats?: string[] }): NativeBarcodeDetectorInstance
}
type ScanProfile = {
  key: string
  label: string
  nativeFormats: string[]
  zxingFormats: BarcodeFormat[]
}
type ScanWindowPreset = {
  key: string
  widthRatio: number
  heightRatio: number
  contrast: number
}

let activeMediaStream: MediaStream | null = null
let scanLoopTimer: number | null = null
let scanCanvas: HTMLCanvasElement | null = null
let scanProfileIndex = 0
let scanAttemptCount = 0
const nativeDetectorCache = new Map<string, NativeBarcodeDetectorInstance | null>()

const serialStatusOptions: EquipmentStatus[] = [
  'available',
  'reserved',
  'checked-out',
  'maintenance',
  'retired',
  'lost',
]

const DESKTOP_SCAN_WINDOW_WIDTH_RATIO = 0.62
const MOBILE_SCAN_WINDOW_WIDTH_RATIO = 0.72
const DESKTOP_SCAN_WINDOW_HEIGHT_RATIO = 0.24
const MOBILE_SCAN_WINDOW_HEIGHT_RATIO = 0.30
const SCAN_LOOP_INTERVAL_MS = 55
const SCAN_CAPTURE_MAX_WIDTH = 1600
const SCAN_PROFILES: ScanProfile[] = [
  {
    key: 'fast-1d',
    label: 'Fast 1D',
    nativeFormats: ['code_128', 'code_39', 'code_93', 'codabar', 'itf', 'upc_a', 'upc_e', 'ean_13', 'ean_8'],
    zxingFormats: [
      BarcodeFormat.CODE_128,
      BarcodeFormat.CODE_39,
      BarcodeFormat.CODE_93,
      BarcodeFormat.CODABAR,
      BarcodeFormat.ITF,
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
    ],
  },
  {
    key: 'matrix-2d',
    label: '2D / matrix',
    nativeFormats: ['qr_code', 'data_matrix', 'pdf417', 'aztec'],
    zxingFormats: [BarcodeFormat.QR_CODE, BarcodeFormat.DATA_MATRIX, BarcodeFormat.PDF_417, BarcodeFormat.AZTEC],
  },
]

const filteredTypes = computed(() => {
  const needle = modelSearch.value.trim().toLowerCase()
  const rows = inventoryStore.types
  if (!needle) return rows
  return rows.filter((type) => {
    return [type.name, type.make, type.model, type.descriptor || '']
      .join(' ')
      .toLowerCase()
      .includes(needle)
  })
})

const canSaveSerial = computed(() => {
  return Boolean(lastDetectedCode.value.trim()) && selectedEquipmentTypeId.value > 0
})
const showDiagnosticsPanel = computed(() => {
  if (isMobileLayout.value) return false
  return auth0Store.isAdmin || auth0Store.hasPermission('view_audit_logs')
})
const diagnosticsLastUpdatedLabel = computed(() => {
  if (!diagnosticsLastUpdatedAt.value) return 'never'
  return diagnosticsLastUpdatedAt.value.toLocaleTimeString()
})
const selectedCameraLabel = computed(() => {
  if (!cameraDevices.value.length) return 'No camera devices found'
  const match = cameraDevices.value.find((camera) => camera.deviceId === selectedCameraId.value)
  if (!match) return cameraDevices.value[0]?.label || 'Select camera'
  return match.label || `Camera ${match.deviceId.slice(0, 6)}`
})
const selectedModelLabel = computed(() => {
  if (!selectedEquipmentTypeId.value) return 'Select a model'
  const selected = inventoryStore.types.find((type) => Number(type.id) === Number(selectedEquipmentTypeId.value))
  if (!selected) return 'Select a model'
  return `${selected.name} · ${selected.make} ${selected.model}`
})

function updateMobileLayoutMode() {
  if (typeof window === 'undefined') return
  const mobileUA = /android|iphone|ipad|ipod|mobile/i.test(window.navigator.userAgent || '')
  isMobileLayout.value = window.matchMedia('(max-width: 900px)').matches || mobileUA
  showAssignmentPanel.value = !isMobileLayout.value
}

function shouldAutoRouteScans() {
  return !isRemotePublisherMode() && (!isMobileLayout.value || mobileDesktopFlowEnabled.value)
}

function normalizedRemoteSessionCode() {
  return remoteSessionCode.value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-_]/g, '')
    .slice(0, 64)
}

function remoteSourceTag() {
  const session = normalizedRemoteSessionCode()
  return session ? `remote:${session}` : ''
}

function isRemotePublisherMode() {
  return isMobileLayout.value && mobileRemoteModeEnabled.value && Boolean(remoteSourceTag())
}

function isRemoteDesktopListenerMode() {
  return !isMobileLayout.value && desktopRemoteListenEnabled.value && Boolean(remoteSourceTag())
}

function buildInlineScanHints(formats: BarcodeFormat[]) {
  const hints = new Map<DecodeHintType, unknown>()
  hints.set(DecodeHintType.TRY_HARDER, true)
  hints.set(DecodeHintType.POSSIBLE_FORMATS, formats)
  return hints
}

function getPreferredCameraId(devices: MediaDeviceInfo[]) {
  const ranked = devices
    .filter((device) => Boolean(device.deviceId))
    .map((device) => {
      const label = String(device.label || '').toLowerCase()
      let score = 0
      if (label.includes('back') || label.includes('rear') || label.includes('environment')) score += 60
      if (label.includes('external') || label.includes('usb')) score += 30
      if (label.includes('front') || label.includes('facetime') || label.includes('integrated')) score -= 10
      return { device, score }
    })
    .sort((left, right) => right.score - left.score)

  return ranked[0]?.device.deviceId || devices[0]?.deviceId || ''
}

function buildScanConstraints(): MediaStreamConstraints {
  const baseVideo: MediaTrackConstraints = {
    width: { ideal: isMobileLayout.value ? 1280 : 1920 },
    height: { ideal: isMobileLayout.value ? 720 : 1080 },
    frameRate: { ideal: 30 },
  }

  if (selectedCameraId.value) {
    return {
      audio: false,
      video: {
        ...baseVideo,
        deviceId: { exact: selectedCameraId.value },
      },
    }
  }

  return {
    audio: false,
    video: {
      ...baseVideo,
      facingMode: { ideal: 'environment' },
    },
  }
}

function getNativeBarcodeDetectorConstructor(): NativeBarcodeDetectorConstructor | null {
  if (typeof window === 'undefined') return null
  const windowWithDetector = window as typeof window & { BarcodeDetector?: NativeBarcodeDetectorConstructor }
  return windowWithDetector.BarcodeDetector ?? null
}

function getNativeBarcodeDetector(profile: ScanProfile): NativeBarcodeDetectorInstance | null {
  const cacheKey = profile.key
  if (nativeDetectorCache.has(cacheKey)) {
    return nativeDetectorCache.get(cacheKey) ?? null
  }
  const Detector = getNativeBarcodeDetectorConstructor()
  if (!Detector) {
    nativeDetectorCache.set(cacheKey, null)
    return null
  }
  try {
    const detector = new Detector({ formats: profile.nativeFormats })
    nativeDetectorCache.set(cacheKey, detector)
    return detector
  } catch {
    nativeDetectorCache.set(cacheKey, null)
    return null
  }
}

function getScanCanvas(width: number, height: number) {
  if (typeof document === 'undefined') return null
  if (!scanCanvas) {
    scanCanvas = document.createElement('canvas')
  }
  if (scanCanvas.width !== width) scanCanvas.width = width
  if (scanCanvas.height !== height) scanCanvas.height = height
  return scanCanvas
}

function captureScanFrame(videoElement: HTMLVideoElement, preset: ScanWindowPreset) {
  const sourceWidth = videoElement.videoWidth
  const sourceHeight = videoElement.videoHeight
  if (!sourceWidth || !sourceHeight) return null

  const cropWidth = Math.max(1, Math.round(sourceWidth * preset.widthRatio))
  const cropHeight = Math.max(1, Math.round(sourceHeight * preset.heightRatio))
  const offsetX = Math.max(0, Math.round((sourceWidth - cropWidth) / 2))
  const offsetY = Math.max(0, Math.round((sourceHeight - cropHeight) / 2))
  const scale = Math.min(1, SCAN_CAPTURE_MAX_WIDTH / cropWidth)
  const targetWidth = Math.max(1, Math.round(cropWidth * scale))
  const targetHeight = Math.max(1, Math.round(cropHeight * scale))
  const canvas = getScanCanvas(targetWidth, targetHeight)
  if (!canvas) return null
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) return null

  context.filter = `contrast(${preset.contrast}) brightness(1.03) saturate(0.9)`
  context.drawImage(videoElement, offsetX, offsetY, cropWidth, cropHeight, 0, 0, targetWidth, targetHeight)
  context.filter = 'none'
  return canvas
}

function getScanWindowPresets(profile: ScanProfile): ScanWindowPreset[] {
  if (profile.key === 'fast-1d') {
    if (isMobileLayout.value) {
      return [
        { key: 'primary-mobile-1d', widthRatio: 0.72, heightRatio: 0.3, contrast: 1.08 },
        { key: 'relaxed-mobile-1d', widthRatio: 0.84, heightRatio: 0.38, contrast: 1.02 },
      ]
    }
    return [
      { key: 'primary-desktop-1d', widthRatio: 0.62, heightRatio: 0.24, contrast: 1.14 },
      { key: 'relaxed-desktop-1d', widthRatio: 0.76, heightRatio: 0.34, contrast: 1.06 },
    ]
  }

  if (isMobileLayout.value) {
    return [{ key: 'mobile-2d', widthRatio: 0.78, heightRatio: 0.42, contrast: 1.04 }]
  }

  return [{ key: 'desktop-2d', widthRatio: 0.68, heightRatio: 0.32, contrast: 1.04 }]
}

async function optimizeActiveTrack(track: MediaStreamTrack) {
  try {
    await track.applyConstraints({
      advanced: [
        { focusMode: 'continuous' } as MediaTrackConstraintSet,
        { exposureMode: 'continuous' } as MediaTrackConstraintSet,
        { whiteBalanceMode: 'continuous' } as MediaTrackConstraintSet,
      ],
    })
  } catch {}
}

async function attachPreviewStream(videoElement: HTMLVideoElement) {
  const stream = await navigator.mediaDevices.getUserMedia(buildScanConstraints())
  activeMediaStream = stream
  videoElement.srcObject = stream
  await videoElement.play()
  const [track] = stream.getVideoTracks()
  if (track) {
    await optimizeActiveTrack(track)
  }
  if (!cameraDevices.value.some((camera) => camera.label)) {
    await loadCameras()
  }
}

async function decodeWithNativeDetector(canvas: HTMLCanvasElement, profile: ScanProfile) {
  const detector = getNativeBarcodeDetector(profile)
  if (!detector) return null
  try {
    const results = await detector.detect(canvas)
    const match = results.find((result) => String(result.rawValue || '').trim())
    return match ? String(match.rawValue || '').trim() : null
  } catch {
    return null
  }
}

function decodeWithZxing(canvas: HTMLCanvasElement, profile: ScanProfile) {
  if (!scanner.value) {
    scanner.value = new BrowserMultiFormatReader(buildInlineScanHints(profile.zxingFormats), {
      delayBetweenScanAttempts: 90,
      delayBetweenScanSuccess: 250,
    })
  } else {
    scanner.value.setHints(buildInlineScanHints(profile.zxingFormats))
  }

  try {
    const result = scanner.value.decodeFromCanvas(canvas)
    return result?.getText()?.trim() || null
  } catch (error) {
    if (error instanceof NotFoundException) return null
    return null
  }
}

function clearScanLoop() {
  if (scanLoopTimer !== null) {
    window.clearTimeout(scanLoopTimer)
    scanLoopTimer = null
  }
}

function stopActiveMediaStream() {
  if (activeMediaStream) {
    activeMediaStream.getTracks().forEach((track) => track.stop())
    activeMediaStream = null
  }
  if (videoEl.value) {
    videoEl.value.pause()
    videoEl.value.srcObject = null
  }
}

function notify(message: string, type: 'success' | 'error' | 'warning' = 'success') {
  try {
    window.dispatchEvent(new CustomEvent('toast', { detail: { type, message } }))
  } catch {}
}

function formatEventTime(value: string) {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString()
}

function routeBadgeClass(route: string) {
  if (route === 'existing_checked_out') return 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/30'
  if (route === 'existing_not_checked_out') return 'bg-primary-500/20 text-primary-200 border border-primary-500/30'
  return 'bg-amber-500/20 text-amber-200 border border-amber-500/30'
}

function parseError(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'object' && error && 'message' in error) {
    const msg = (error as { message?: string }).message
    if (msg) return msg
  }
  return fallback
}

async function loadCategories() {
  const { data, error } = await supabase
    .schema('app' as any)
    .from('categories')
    .select('id,name')
    .order('name')
  if (error) throw error
  categories.value = (data || []) as Array<{ id: number; name: string }>
}

async function loadScanEvents() {
  if (!showDiagnosticsPanel.value) return
  scanEventsLoading.value = true
  scanEventsError.value = ''
  try {
    const { data, error } = await supabase
      .schema('app' as any)
      .from('scan_events')
      .select('id,scanned_code,route,equipment_serial_id,checkout_id,metadata,created_at')
      .order('created_at', { ascending: false })
      .limit(20)
    if (error) throw error
    scanEvents.value = (data || []) as typeof scanEvents.value
    diagnosticsLastUpdatedAt.value = new Date()
  } catch (error) {
    scanEventsError.value = parseError(error, 'Failed to load scan diagnostics.')
  } finally {
    scanEventsLoading.value = false
  }
}

async function publishRemoteScan(code: string) {
  const source = remoteSourceTag()
  if (!source) {
    notify('Remote scanner mode requires a session code.', 'error')
    return false
  }
  try {
    await resolveScannedCode(code, source)
    notify(`Sent ${code} to remote session ${normalizedRemoteSessionCode()}.`)
    return true
  } catch (error) {
    notify(parseError(error, 'Failed to send scan to remote listener.'), 'error')
    return false
  }
}

async function primeRemoteListenerCursor() {
  const source = remoteSourceTag()
  if (!source) return
  try {
    const { data, error } = await supabase
      .schema('app' as any)
      .rpc('scan_get_remote_cursor', { p_source: source })
    if (error) throw error
    const newestId = Number(data || 0)
    // Start at the newest seen event so returning to the page does not
    // replay stale scans from a prior session.
    remoteLastSeenEventId.value = Number.isFinite(newestId) ? Math.max(0, newestId) : 0
  } catch (error) {
    remoteListenerError.value = parseError(error, 'Could not initialize remote listener.')
  }
}

async function pollRemoteScanEvents() {
  if (!isRemoteDesktopListenerMode() || remoteListenerBusy.value) return
  const source = remoteSourceTag()
  if (!source) return

  remoteListenerBusy.value = true
  try {
    const { data, error } = await supabase
      .schema('app' as any)
      .rpc('scan_consume_remote_events', {
        p_source: source,
        p_after_id: remoteLastSeenEventId.value,
        p_limit: 20,
      })
    if (error) throw error

    const rows = (data || []) as Array<{ id: number; scanned_code: string | null; created_at: string }>
    for (const row of rows) {
      const eventId = Number(row.id || 0)
      if (eventId > remoteLastSeenEventId.value) {
        remoteLastSeenEventId.value = eventId
      }
      const code = String(row.scanned_code || '').trim()
      if (!code) continue
      lastDetectedCode.value = code
      await routeScannedCode(code, 'remote_desktop_consume')
    }
    remoteListenerError.value = ''
  } catch (error) {
    remoteListenerError.value = parseError(error, 'Remote listener failed to poll scan events.')
  } finally {
    remoteListenerBusy.value = false
  }
}

async function startRemoteListenerPolling() {
  if (!isRemoteDesktopListenerMode()) return
  await primeRemoteListenerCursor()
  if (remotePollTimer !== null) {
    window.clearInterval(remotePollTimer)
    remotePollTimer = null
  }
  remotePollTimer = window.setInterval(() => {
    void pollRemoteScanEvents()
  }, 1500)
  await pollRemoteScanEvents()
}

function stopRemoteListenerPolling() {
  if (remotePollTimer !== null) {
    window.clearInterval(remotePollTimer)
    remotePollTimer = null
  }
  remoteLastSeenEventId.value = 0
}

function resetTransientScanUiState() {
  showUnknownSerialModal.value = false
  pendingUnknownSerial.value = ''
  showCheckinModal.value = false
  showShippingLabelModal.value = false
  resetAutoFlow()
}

async function loadCameras() {
  try {
    cameraDevices.value = await BrowserMultiFormatReader.listVideoInputDevices()
    const selectedCameraStillExists = cameraDevices.value.some((camera) => camera.deviceId === selectedCameraId.value)
    if (cameraDevices.value.length && (!selectedCameraId.value || !selectedCameraStillExists)) {
      selectedCameraId.value = getPreferredCameraId(cameraDevices.value)
    }
    if (!cameraDevices.value.length) {
      cameraError.value = 'No camera was found on this device.'
    }
  } catch (error) {
    cameraError.value = parseError(error, 'Unable to access camera devices.')
  }
}

function closeInlineDropdowns() {
  showCameraDropdown.value = false
  showModelDropdown.value = false
}

function toggleCameraDropdown() {
  showCameraDropdown.value = !showCameraDropdown.value
  if (showCameraDropdown.value) {
    showModelDropdown.value = false
  }
}

function toggleModelDropdown() {
  showModelDropdown.value = !showModelDropdown.value
  if (showModelDropdown.value) {
    showCameraDropdown.value = false
  }
}

function selectCamera(deviceId: string) {
  selectedCameraId.value = deviceId
  showCameraDropdown.value = false
}

function selectModel(modelId: number) {
  selectedEquipmentTypeId.value = modelId
  showModelDropdown.value = false
}

async function handleDetectedCode(rawValue: string) {
  const value = rawValue.trim()
  if (!value) return false

  if (scannedSessionCodes.value.includes(value)) {
    stopScan()
    notify(`Duplicate scan detected for ${value}.`, 'warning')
    return true
  }

  scannedSessionCodes.value = [...scannedSessionCodes.value, value]
  lastDetectedCode.value = value
  stopScan()
  if (isRemotePublisherMode()) {
    await publishRemoteScan(value)
    return true
  }
  if (shouldAutoRouteScans()) {
    await routeScannedCode(value)
  } else {
    notify('Scan captured. Use manual assignment controls below.', 'success')
  }
  return true
}

function stopScan() {
  clearScanLoop()
  stopActiveMediaStream()
  scanAttemptCount = 0
  isScanning.value = false
  isScannerBusy.value = false
}

function resetAutoFlow() {
  autoFlowCheckoutId.value = null
  autoFlowTypeId.value = null
  autoFlowMake.value = ''
  autoFlowModel.value = ''
  autoFlowSerial.value = ''
}

function handleAutoFlowSaved() {
  showCheckinModal.value = false
  showShippingLabelModal.value = false
  resetAutoFlow()
  try {
    window.dispatchEvent(new CustomEvent('testdrive:data-updated'))
  } catch {}
}

async function startScan() {
  cameraError.value = ''
  if (!videoEl.value) {
    cameraError.value = 'Camera preview is not available yet.'
    return
  }
  if (!cameraDevices.value.length) {
    await loadCameras()
    if (!cameraDevices.value.length) return
  }
  stopScan()
  scanProfileIndex = 0
  scanAttemptCount = 0
  const videoElement = videoEl.value as unknown as HTMLVideoElement

  try {
    isScanning.value = true
    await attachPreviewStream(videoElement)

    const scanTick = async () => {
      if (!isScanning.value || !videoEl.value) return
      const preview = videoEl.value as unknown as HTMLVideoElement
      if (preview.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || !preview.videoWidth || !preview.videoHeight) {
        scanLoopTimer = window.setTimeout(() => void scanTick(), SCAN_LOOP_INTERVAL_MS)
        return
      }

      scanAttemptCount += 1
      const shouldTryMatrix = scanAttemptCount % 6 === 0
      scanProfileIndex = shouldTryMatrix ? 1 : 0
      const profile = SCAN_PROFILES[scanProfileIndex]

      try {
        for (const preset of getScanWindowPresets(profile)) {
          const canvas = captureScanFrame(preview, preset)
          if (!canvas) continue

          const nativeValue = await decodeWithNativeDetector(canvas, profile)
          if (nativeValue) {
            await handleDetectedCode(nativeValue)
            return
          }

          const zxingValue = decodeWithZxing(canvas, profile)
          if (zxingValue) {
            await handleDetectedCode(zxingValue)
            return
          }
        }
      } catch (error) {
        cameraError.value = parseError(error, `Scan error while checking ${profile.label}. Try another angle or move closer.`)
      }

      if (!isScanning.value) return
      scanLoopTimer = window.setTimeout(() => void scanTick(), SCAN_LOOP_INTERVAL_MS)
    }

    scanLoopTimer = window.setTimeout(() => void scanTick(), SCAN_LOOP_INTERVAL_MS)
  } catch (error) {
    cameraError.value = parseError(error, 'Unable to start scanner. Verify camera permissions.')
    stopScan()
  }
}

async function startManagedScan() {
  if (isScannerBusy.value || isScanning.value) return
  isScannerBusy.value = true
  try {
    await startScan()
  } finally {
    isScannerBusy.value = false
  }
}

function cancelManagedScan() {
  stopScan()
  isScannerBusy.value = false
  notify('Scanner closed.')
}

async function rescan() {
  lastDetectedCode.value = ''
  scannedSessionCodes.value = []
  resetAutoFlow()
  showCheckinModal.value = false
  showShippingLabelModal.value = false
  showUnknownSerialModal.value = false
  await startManagedScan()
}

function onGlobalKeydown(event: KeyboardEvent) {
  const target = event.target as HTMLElement | null
  const isTypingTarget =
    !!target &&
    (target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable)
  if (isTypingTarget) return

  if (event.key === 'Escape') {
    event.preventDefault()
    closeInlineDropdowns()
    if (isScannerBusy.value || isScanning.value) {
      cancelManagedScan()
    }
    return
  }

  if (event.ctrlKey && event.shiftKey && (event.key === 's' || event.key === 'S')) {
    event.preventDefault()
    if (isScannerBusy.value || isScanning.value) {
      cancelManagedScan()
      return
    }
    void startManagedScan()
  }
}

async function routeScannedCode(rawCode: string, source = 'web_scan') {
  const code = rawCode.trim()
  if (!code) return

  let resolved
  try {
    resolved = await resolveScannedCode(code, source)
  } catch (error) {
    notify(parseError(error, 'Unable to resolve scanned serial.'), 'error')
    return
  }

  if (resolved.route === 'new_serial' || !resolved.equipment_serial_id || !resolved.equipment_type_id) {
    pendingUnknownSerial.value = code
    showUnknownSerialModal.value = true
    notify('Serial not found. Choose a model to register it.', 'warning')
    return
  }

  const canonicalSerial = String(resolved.serial || code)
  autoFlowTypeId.value = Number(resolved.equipment_type_id)
  autoFlowMake.value = String(resolved.make || 'Unknown Make')
  autoFlowModel.value = String(resolved.model || 'Unknown Model')
  autoFlowSerial.value = canonicalSerial
  autoFlowCheckoutId.value = resolved.checkout_id ? Number(resolved.checkout_id) : null

  if (resolved.route === 'existing_checked_out') {
    if (!autoFlowCheckoutId.value) {
      notify('Serial is checked out but no active checkout record was found.', 'error')
      return
    }
    showCheckinModal.value = true
    notify(`Checked-out serial found. Opening check-in for ${canonicalSerial}.`)
    return
  }

  showShippingLabelModal.value = true
  notify(`Known serial found (${canonicalSerial}). Opening shipping label flow.`)
}

async function createQuickModel() {
  const name = quickCreateForm.value.name.trim()
  const make = quickCreateForm.value.make.trim()
  const model = quickCreateForm.value.model.trim()
  if (!name || !make || !model) {
    notify('Name, make, and model are required to create a model.', 'error')
    return
  }
  creatingModel.value = true
  try {
    const equipmentTypeId = await createEquipmentTypeWithDetails({
      name,
      make,
      model,
      categoryId: quickCreateForm.value.categoryId || null,
      status: 'active',
      products: ['POTS Repl'],
    })
    await inventoryStore.loadTypes()
    selectedEquipmentTypeId.value = equipmentTypeId
    showQuickCreate.value = false
    quickCreateForm.value = { name: '', make: '', model: '', categoryId: 0 }
    notify('Equipment model created and selected.')

    // In unknown-serial intake flow, auto-save the queued scanned serial so
    // the new model immediately has inventory and appears in Picker.
    const queuedSerial = pendingUnknownSerial.value.trim()
    if (queuedSerial) {
      const saved = await saveSerialValue(queuedSerial)
      if (!saved) {
        notify('Model created, but serial save failed. Use Save New Serial.', 'warning')
        return
      }
      notify(`Serial ${queuedSerial} saved to new model.`)
    }
  } catch (error) {
    notify(parseError(error, 'Failed to create equipment model.'), 'error')
  } finally {
    creatingModel.value = false
  }
}

async function saveScannedSerial() {
  const serial = lastDetectedCode.value.trim()
  if (!serial || selectedEquipmentTypeId.value <= 0) {
    notify('Scan a code and select a model before saving.', 'error')
    return
  }
  await saveSerialValue(serial)
}

async function saveSerialValue(serial: string): Promise<boolean> {
  if (!serial || selectedEquipmentTypeId.value <= 0) {
    notify('Select a model before saving the serial.', 'error')
    return false
  }

  savingSerial.value = true
  try {
    const { data: duplicate, error: duplicateError } = await supabase
      .from('equipment_serials_v')
      .select('id,serial')
      .ilike('serial', serial)
      .maybeSingle()
    if (duplicateError) throw duplicateError
    if (duplicate?.id) {
      notify(`Serial ${duplicate.serial || serial} already exists.`, 'error')
      return false
    }

    await createEquipmentSerial({
      equipmentTypeId: selectedEquipmentTypeId.value,
      serial,
      barcode: serial,
      status: serialStatus.value,
      notes: serialNotes.value || 'Scanned via camera',
    })
    notify(`Saved serial ${serial}.`)
    try {
      window.dispatchEvent(new CustomEvent('testdrive:data-updated'))
    } catch {}
    lastDetectedCode.value = ''
    serialNotes.value = ''
    showUnknownSerialModal.value = false
    pendingUnknownSerial.value = ''
    return true
  } catch (error) {
    notify(parseError(error, 'Failed to save serial.'), 'error')
    return false
  } finally {
    savingSerial.value = false
  }
}

function openManualFromUnknown() {
  if (!pendingUnknownSerial.value) return
  lastDetectedCode.value = pendingUnknownSerial.value
  showUnknownSerialModal.value = false
  showAssignmentPanel.value = true
}

function openQuickCreateFromUnknown() {
  showQuickCreate.value = true
  showUnknownSerialModal.value = false
  showAssignmentPanel.value = true
}

async function saveUnknownSerialNow() {
  const serial = pendingUnknownSerial.value.trim()
  if (!serial) {
    notify('No unknown serial is queued.', 'error')
    return
  }
  const selectedTypeId = Number(selectedEquipmentTypeId.value)
  const saved = await saveSerialValue(serial)
  if (!saved || !selectedTypeId) return
  notify(`Serial ${serial} saved.`)
}

onMounted(async () => {
  resetTransientScanUiState()
  updateMobileLayoutMode()
  try {
    const raw = window.localStorage.getItem(MOBILE_DESKTOP_FLOW_KEY)
    mobileDesktopFlowEnabled.value = raw === 'true'
    const mobileRemoteRaw = window.localStorage.getItem(MOBILE_REMOTE_MODE_KEY)
    mobileRemoteModeEnabled.value = mobileRemoteRaw === 'true'
    const desktopListenRaw = window.localStorage.getItem(DESKTOP_REMOTE_LISTEN_KEY)
    desktopRemoteListenEnabled.value = desktopListenRaw === null ? true : desktopListenRaw === 'true'
    const sessionRaw = window.localStorage.getItem(REMOTE_SESSION_CODE_KEY)
    if (sessionRaw && sessionRaw.trim()) {
      remoteSessionCode.value = sessionRaw.trim()
    }
  } catch {}
  void Promise.all([inventoryStore.loadTypes(), loadCategories()]).catch((error) => {
    notify(parseError(error, 'Unable to load scanner setup data.'), 'error')
  })
  void loadCameras()
  if (showDiagnosticsPanel.value) {
    await loadScanEvents()
    diagnosticsPollTimer = window.setInterval(() => {
      void loadScanEvents()
    }, 15000)
  }
  window.addEventListener('keydown', onGlobalKeydown)
  window.addEventListener('click', closeInlineDropdowns)
  window.addEventListener('resize', updateMobileLayoutMode)
  if (isRemoteDesktopListenerMode()) {
    await startRemoteListenerPolling()
  }
})

watch(mobileDesktopFlowEnabled, (enabled) => {
  try {
    window.localStorage.setItem(MOBILE_DESKTOP_FLOW_KEY, enabled ? 'true' : 'false')
  } catch {}
})

watch(mobileRemoteModeEnabled, (enabled) => {
  try {
    window.localStorage.setItem(MOBILE_REMOTE_MODE_KEY, enabled ? 'true' : 'false')
  } catch {}
})

watch(desktopRemoteListenEnabled, (enabled) => {
  try {
    window.localStorage.setItem(DESKTOP_REMOTE_LISTEN_KEY, enabled ? 'true' : 'false')
  } catch {}
})

watch(remoteSessionCode, (value) => {
  try {
    window.localStorage.setItem(REMOTE_SESSION_CODE_KEY, value.trim())
  } catch {}
})

watch(
  () => [isMobileLayout.value, desktopRemoteListenEnabled.value, remoteSessionCode.value],
  async () => {
    if (isRemoteDesktopListenerMode()) {
      await startRemoteListenerPolling()
      return
    }
    stopRemoteListenerPolling()
  },
)

onBeforeUnmount(() => {
  stopScan()
  scanner.value = null
  if (diagnosticsPollTimer !== null) {
    window.clearInterval(diagnosticsPollTimer)
    diagnosticsPollTimer = null
  }
  window.removeEventListener('keydown', onGlobalKeydown)
  window.removeEventListener('click', closeInlineDropdowns)
  window.removeEventListener('resize', updateMobileLayoutMode)
  stopRemoteListenerPolling()
  resetTransientScanUiState()
})
</script>

<style scoped>
.scan-input-surface {
  background-color: rgb(30 41 59 / 0.72);
  border-color: rgb(71 85 105 / 0.8);
  color: rgb(241 245 249);
}

.scan-input-surface::placeholder {
  color: rgb(148 163 184);
}

.scan-input-surface:focus {
  border-color: rgb(100 116 139 / 0.9);
  box-shadow: 0 0 0 2px rgb(51 65 85 / 0.45);
}

.scan-select-dark {
  appearance: none;
  color-scheme: dark;
}

.dropdown-spring-enter-active {
  transition:
    opacity 180ms ease,
    transform 360ms cubic-bezier(0.2, 1.05, 0.25, 1);
}

.dropdown-spring-leave-active {
  transition:
    opacity 130ms ease,
    transform 180ms ease;
}

.dropdown-spring-enter-from,
.dropdown-spring-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.98);
}
</style>
